import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { type ArtistApplication, type CreateArtistApplicationData, type UpdateArtistApplicationData } from "@/hooks/useArtistApplications";

interface ArtistApplicationFormProps {
  application?: ArtistApplication | null;
  onSubmit: (data: CreateArtistApplicationData | UpdateArtistApplicationData) => void;
  onCancel: () => void;
  loading?: boolean;
}

const COLLABORATION_MODELS = [
  "Freelance",
  "Part-time",
  "Full-time",
  "Project-based",
  "Contract",
  "Other"
];

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "reviewed", label: "Reviewed" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" }
];

export default function ArtistApplicationForm({
  application,
  onSubmit,
  onCancel,
  loading = false,
}: ArtistApplicationFormProps) {
  const [formData, setFormData] = React.useState({
    full_name: application?.full_name || "",
    email: application?.email || "",
    phone: application?.phone || "",
    portfolio_link: application?.portfolio_link || "",
    art_type: application?.art_type || "",
    bio: application?.bio || "",
    why_brutt: application?.why_brutt || "",
    collaboration_model: application?.collaboration_model || "",
    image_1_url: application?.image_1_url || "",
    image_2_url: application?.image_2_url || "",
    image_3_url: application?.image_3_url || "",
    status: application?.status || "pending",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      phone: formData.phone || undefined,
      image_1_url: formData.image_1_url || undefined,
      image_2_url: formData.image_2_url || undefined,
      image_3_url: formData.image_3_url || undefined,
    };

    if (application) {
      onSubmit({
        id: application.id,
        ...submitData,
      } as UpdateArtistApplicationData);
    } else {
      onSubmit(submitData as CreateArtistApplicationData);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="full_name">Full Name *</Label>
          <Input
            id="full_name"
            value={formData.full_name}
            onChange={(e) => handleInputChange("full_name", e.target.value)}
            placeholder="John Doe"
            required
          />
        </div>
        <div>
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            placeholder="john@example.com"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange("phone", e.target.value)}
            placeholder="+1234567890"
          />
        </div>
        <div>
          <Label htmlFor="art_type">Art Type *</Label>
          <Input
            id="art_type"
            value={formData.art_type}
            onChange={(e) => handleInputChange("art_type", e.target.value)}
            placeholder="Digital Art, Painting, Sculpture, etc."
            required
          />
        </div>
      </div>

      <div>
        <Label htmlFor="portfolio_link">Portfolio Link *</Label>
        <Input
          id="portfolio_link"
          type="url"
          value={formData.portfolio_link}
          onChange={(e) => handleInputChange("portfolio_link", e.target.value)}
          placeholder="https://your-portfolio.com"
          required
        />
      </div>

      <div>
        <Label htmlFor="bio">Bio *</Label>
        <Textarea
          id="bio"
          value={formData.bio}
          onChange={(e) => handleInputChange("bio", e.target.value)}
          rows={3}
          placeholder="Tell us about yourself and your artistic background..."
          required
        />
      </div>

      <div>
        <Label htmlFor="why_brutt">Why Brutt? *</Label>
        <Textarea
          id="why_brutt"
          value={formData.why_brutt}
          onChange={(e) => handleInputChange("why_brutt", e.target.value)}
          rows={3}
          placeholder="Why do you want to collaborate with Brutt?"
          required
        />
      </div>

      <div>
        <Label htmlFor="collaboration_model">Collaboration Model *</Label>
        <Select
          value={formData.collaboration_model}
          onValueChange={(value) => handleInputChange("collaboration_model", value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select collaboration model" />
          </SelectTrigger>
          <SelectContent>
            {COLLABORATION_MODELS.map((model) => (
              <SelectItem key={model} value={model}>
                {model}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="image_1_url">Image 1 URL</Label>
          <Input
            id="image_1_url"
            type="url"
            value={formData.image_1_url}
            onChange={(e) => handleInputChange("image_1_url", e.target.value)}
            placeholder="https://example.com/image1.jpg"
          />
        </div>
        <div>
          <Label htmlFor="image_2_url">Image 2 URL</Label>
          <Input
            id="image_2_url"
            type="url"
            value={formData.image_2_url}
            onChange={(e) => handleInputChange("image_2_url", e.target.value)}
            placeholder="https://example.com/image2.jpg"
          />
        </div>
        <div>
          <Label htmlFor="image_3_url">Image 3 URL</Label>
          <Input
            id="image_3_url"
            type="url"
            value={formData.image_3_url}
            onChange={(e) => handleInputChange("image_3_url", e.target.value)}
            placeholder="https://example.com/image3.jpg"
          />
        </div>
      </div>

      {application && (
        <div>
          <Label htmlFor="status">Status</Label>
          <Select
            value={formData.status}
            onValueChange={(value) => handleInputChange("status", value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? "Saving..." : application ? "Update Application" : "Create Application"}
        </Button>
      </div>
    </form>
  );
}
